import { useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { getUserLocationFromIP } from '../services/geolocationService';
import { clearUserProjectData } from '../utils/cesiumHelpers';
import { eventBus } from '../events';
import { markInitialFlyComplete } from '../utils/onboardingFlags';
// Pre-warm the IP geolocation lookup at module load so its network round-trip
// overlaps the initial title / pre-commitment window. By the time the viewer is
// ready the location is usually already resolved, so the zoom starts without a
// network wait. The service resolves to a fallback on error, so this never rejects.
const userLocationPromise = getUserLocationFromIP();
export function useAuthNavigation(viewer, isAuthenticated, isLoading, modalActions) {
    const skipNextDefaultFlyRef = useRef(false);
    const hasFlownRef = useRef(false);
    // A demo project selection owns the camera; skip the next default fly so we
    // don't yank the view away from a demo the visitor just opened.
    useEffect(() => {
        const unsubscribe = eventBus.on('demo:projectSelected', () => {
            skipNextDefaultFlyRef.current = true;
        });
        return unsubscribe;
    }, []);
    // Center on the visitor's approximate location on first load — for EVERYONE,
    // signed in or not. Fires once, as soon as the viewer exists; the pre-warmed
    // lookup keeps it snappy and the 2.5s flight stays for polish.
    useEffect(() => {
        if (!viewer || hasFlownRef.current)
            return;
        hasFlownRef.current = true;
        // Signal completion (latch + event) so the first-run tour can trigger after
        // the flight. Called on every path — skipped, destroyed, or flight-complete —
        // so the tour never waits on a fly that won't finish.
        const signalFlyComplete = () => {
            markInitialFlyComplete();
            eventBus.emit('camera:initialFlyComplete');
        };
        userLocationPromise.then((location) => {
            if (skipNextDefaultFlyRef.current) {
                skipNextDefaultFlyRef.current = false;
                signalFlyComplete();
                return;
            }
            if (viewer.isDestroyed()) {
                signalFlyComplete();
                return;
            }
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(location.lng, location.lat, 6000000),
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-90.0),
                    roll: 0.0
                },
                duration: 2.5,
                complete: signalFlyComplete
            });
        });
    }, [viewer]);
    // Prompt login when a stored/expired session fails to authenticate.
    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            const hadTokens = localStorage.getItem('landscope_session_id_cleared') === 'true';
            const hasExistingSession = localStorage.getItem('landscope_session_id');
            if (hadTokens) {
                modalActions.openLogin();
                localStorage.removeItem('landscope_session_id_cleared');
            }
            else if (hasExistingSession) {
                modalActions.openLogin();
            }
        }
    }, [isAuthenticated, isLoading]);
    // Clear user project data from the map when the user logs out.
    useEffect(() => {
        if (!isAuthenticated && viewer) {
            clearUserProjectData(viewer);
        }
    }, [isAuthenticated, viewer]);
}
